import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Star, User, Home, ChevronRight, Sparkles } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { Card, CardContent } from '../../components/ui/Card';
import { useScreenInit } from '../../useScreenInit.js';
import { APARTMENTS, scoreColor } from './appartamentiData';
import { initials } from '../../context/PlatformContext';
function avg(arr: number[]): number {
  if (arr.length === 0) return 0;
  return Math.round(arr.reduce((s, n) => s + n, 0) / arr.length);
}
function barColor(score: number): string {
  if (score >= 95) return 'bg-emerald-500';
  if (score >= 88) return 'bg-teal-deep';
  if (score >= 80) return 'bg-amber-500';
  return 'bg-red-500';
}
export function CmQualitaPage() {
  useScreenInit();
  const navigate = useNavigate();
  const allScores = useMemo(() => {
    const s: number[] = [];
    APARTMENTS.forEach(a => a.interventions.forEach(i => {
      if (i.score != null) s.push(i.score);
    }));
    return s;
  }, []);
  const overall = avg(allScores);
  const totalChecks = allScores.length;
  const perCleaner = useMemo(() => {
    const map: Record<string, number[]> = {};
    APARTMENTS.forEach(a => a.interventions.forEach(i => {
      if (i.score != null) {
        map[i.cleaner] = map[i.cleaner] || [];
        map[i.cleaner].push(i.score);
      }
    }));
    return Object.entries(map).map(([cleaner, scores]) => ({
      cleaner,
      score: avg(scores),
      count: scores.length
    })).sort((a, b) => b.score - a.score);
  }, []);
  const perApartment = useMemo(() => {
    return APARTMENTS.map(a => {
      const scores = a.interventions.filter(i => i.score != null).map(i => i.score as number);
      return {
        id: a.id,
        name: a.name,
        score: avg(scores),
        count: scores.length
      };
    }).filter(a => a.count > 0).sort((a, b) => b.score - a.score);
  }, []);
  return <MobileLayout data-id="element-2840">
      <div className="mb-6" data-id="element-2841">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-2842">Qualità</h1>
        <p className="text-sm text-muted-foreground mt-0.5" data-id="element-2843">
          Punteggi CleanAI aggregati per cleaner e appartamento.
        </p>
      </div>

      {/* Overall */}
      <Card className="mb-6" data-id="element-2844">
        <CardContent className="flex items-center gap-5" data-id="element-2845">
          <div className="relative w-20 h-20 flex-shrink-0" data-id="element-2846">
            <svg viewBox="0 0 36 36" className="w-20 h-20 -rotate-90" data-id="element-2847">
              <circle cx="18" cy="18" r="16" fill="none" className="stroke-muted" strokeWidth="3" data-id="element-2848" />
              <circle cx="18" cy="18" r="16" fill="none" className="stroke-coral" strokeWidth="3" strokeDasharray={`${overall / 100 * 100.5} 100.5`} strokeLinecap="round" data-id="element-2849" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-2850">
              <span className="text-xl font-bold text-navy" data-id="element-2851">{overall}</span>
            </div>
          </div>
          <div data-id="element-2852">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-navy" data-id="element-2853">
              <Sparkles size={15} className="text-coral" data-id="element-2854" />
              Punteggio medio CleanAI
            </div>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-2855">
              Su {totalChecks} controlli prima/dopo registrati.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Per cleaner */}
      <h2 className="text-sm font-semibold text-navy flex items-center gap-2 mb-3" data-id="element-2856">
        <User size={16} className="text-coral" data-id="element-2857" />
        Per cleaner
      </h2>
      <Card className="mb-6" data-id="element-2858">
        <CardContent className="space-y-4" data-id="element-2859">
          {perCleaner.map(c => <div key={c.cleaner} data-id="element-2860">
              <div className="flex items-center justify-between mb-1.5" data-id="element-2861">
                <span className="flex items-center gap-2 text-sm font-medium text-foreground" data-id="element-2862">
                  <span className="w-7 h-7 rounded-full bg-coral/10 flex items-center justify-center text-[10px] font-bold text-coral" data-id="element-2863">
                    {initials(c.cleaner)}
                  </span>
                  {c.cleaner}
                  <span className="text-xs text-muted-foreground" data-id="element-2864">
                    · {c.count} interventi
                  </span>
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${scoreColor(c.score)}`} data-id="element-2865">
                  <Star size={11} data-id="element-2866" />
                  {c.score}
                </span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 overflow-hidden" data-id="element-2867">
                <div className={`h-2 rounded-full transition-all ${barColor(c.score)}`} style={{
              width: `${c.score}%`
            }} data-id="element-2868" />
              </div>
            </div>)}
        </CardContent>
      </Card>

      {/* Per apartment */}
      <h2 className="text-sm font-semibold text-navy flex items-center gap-2 mb-3" data-id="element-2869">
        <Home size={16} className="text-coral" data-id="element-2870" />
        Per appartamento
      </h2>
      <div className="space-y-3" data-id="element-2871">
        {perApartment.map(a => <Card key={a.id} className="hover:border-coral/40 hover:shadow-sm transition-all cursor-pointer" onClick={() => navigate(`/cm/clienti/appartamenti/${a.id}`)} data-id="element-2872">
            <CardContent className="flex items-center justify-between gap-3" data-id="element-2873">
              <div className="min-w-0" data-id="element-2874">
                <div className="text-sm font-semibold text-foreground truncate" data-id="element-2875">
                  {a.name}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5" data-id="element-2876">
                  <CheckCircle size={11} data-id="element-2877" />
                  {a.count} controlli
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0" data-id="element-2878">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${scoreColor(a.score)}`} data-id="element-2879">
                  <Star size={12} data-id="element-2880" />
                  {a.score}
                </span>
                <ChevronRight size={16} className="text-gray-300" data-id="element-2881" />
              </div>
            </CardContent>
          </Card>)}
      </div>
    </MobileLayout>;
}